'use client';

import { JSX, useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  useFloating,
  offset,
  flip,
  shift,
  Placement,
} from '@floating-ui/react';

// components
import { AtIcon } from '@/components/atoms';
import { MlInfoTooltipContent } from './ml-info-tooltip-content';

// types
import { IMlInfoTooltipProps } from './ml-info-tooltip.types';

// hooks
import useBreakpoint from '@/hooks/useBreakpoint';

/**
 * Renders an info icon that triggers a floating tooltip on click (mobile) or hover/keyboard (desktop).
 * Designed for accessibility and responsive behavior.
 *
 */

export const MlInfoTooltip = ({
  body = '',
  iconColor = 'var(--color-ui-icon-primary)',
  iconName,
  tooltipName,
}: IMlInfoTooltipProps): JSX.Element => {
  const bp = useBreakpoint();
  const tooltipId = useId();
  const openedByKeyboard = useRef(false);

  const [isOpen, setIsOpen] = useState(false);
  const [placement, setPlacement] = useState<Placement>('bottom-start');

  const wrapperRef = useRef<HTMLDivElement>(null);
  const isMobile = bp === 'xs' || bp === 'sm' || bp === 'md';

  // only show focus styles on tooltip if keyboard was used
  const lastInteractionWasKeyboard = useRef(false);
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        lastInteractionWasKeyboard.current = true;
      }
    };

    const handleMouseDown = () => {
      lastInteractionWasKeyboard.current = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('mousedown', handleMouseDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);

  const onOpen = () => {
    openedByKeyboard.current = lastInteractionWasKeyboard.current;
    setIsOpen(true);
  };

  const tooltipTriggers = useTooltipTrigger({
    isMobile,
    onOpen,
  });

  const { x, y, strategy, refs } = useFloating({
    placement,
    middleware: [offset({ mainAxis: 12, crossAxis: 32 }), flip(), shift()],
  });

  // useFloating update placement
  useEffect(() => {
    if (!isOpen || !refs.reference.current) return;

    const triggerTop = refs.reference.current.getBoundingClientRect().top;
    const windowHeight = window.innerHeight;
    const isInTopHalf = triggerTop < windowHeight / 2;

    setPlacement(isInTopHalf ? 'bottom-start' : 'top-start');
  }, [isOpen, refs.reference]);

  const onClose = useCallback(() => {
    setIsOpen(false);
    (refs.reference.current as HTMLButtonElement | null)?.focus();
  }, [refs]);

  // close tooltip when user clicks outside of container or uses esc key
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        onClose();
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  return (
    <div className="relative inline-block align-baseline" ref={wrapperRef}>
      <button
        className="p-0.5 text-20"
        aria-label="Open tooltip"
        aria-expanded={isOpen}
        aria-haspopup={true}
        aria-describedby={tooltipId}
        ref={refs.setReference}
        {...tooltipTriggers}
      >
        <AtIcon aria-hidden="true" color={iconColor} icon={iconName} />
      </button>

      {isOpen && (
        <MlInfoTooltipContent
          body={body}
          floatingRef={refs.setFloating}
          floatingStyle={{
            position: strategy,
            top: y ?? 0,
            left: x ?? 0,
          }}
          id={tooltipId}
          shouldAutoFocus={openedByKeyboard.current}
          tooltipName={tooltipName}
          onClose={onClose}
        />
      )}
    </div>
  );
};

// toolTip trigger hook
const useTooltipTrigger = ({
  isMobile,
  onOpen,
}: {
  isMobile: boolean;
  onOpen: () => void;
}) => {
  if (isMobile) {
    return {
      onClick: onOpen,
    };
  }
  return {
    onMouseEnter: onOpen,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onOpen();
      }
    },
  };
};
